import React from 'react'
import { Row, IconButton, colors, H2 } from '@fs/zion-ui'
import { Elevation } from '@fs/zion-ui/quarks-authorized-use-only'
import { css } from '@emotion/core'
import { MenuClose } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'

const windowHeaderCss = css`
  padding: var(--spacer-nano, 4px);
  background-color: ${colors.gray03};
`

const windowCss = css`
  /* Needed in order to properly display ComboViewer */
  overflow: hidden;
  border: 1px solid ${colors.gray10};
  border-start-start-radius: 8px;
  border-start-end-radius: 8px;
  width: 50%;
  display: flex;
  position: relative;
  flex-grow: 1;
  flex-direction: column;
`
const windowContainerCss = css`
  padding: 4px;
  overflow: hidden;
  height: 0;
  flex-grow: 1;
  display: flex;
  gap: var(--spacer-xxs, 12px);
`

// eslint-disable-next-line no-warning-comments
// TODO: remove this when we fix JIRA FRONTIER-911
const viewerWrapperCss = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  position: relative;
`

/**
 * This component is a container for the multiple elements that are displayed with the split screen image viewer
 * @param {Object} props
 * @param {ReactNode} props.MainImageViewer - The main image viewer; can be anything with an ImageViewer base
 * @param {ReactNode} props.MainRibbonViewer - The main ribbon viewer, typically paired with a ComboViewer
 * @param {ReactNode} props.SplitScreenImageViewer - The split screen image viewer; can be anything with an ImageViewer base
 * @param {ReactNode} props.SplitScreenRibbonViewer - The split screen ribbon viewer, typically paired with a ComboViewer
 * @returns {ReactNode} - A nice looking container for two different ImageViewers, and their optional respective RibbonViewers
 */
export default function SplitScreenContainer({
  MainImageViewer,
  MainRibbonViewer,
  mainImageTitleText,
  mainRibbonReady,
  SplitScreenImageViewer,
  SplitScreenRibbonViewer,
  splitScreenTitleText,
  splitScreenRibbonReady,
  splitScreenIsOpen = false,
  setSplitScreenIsOpen,
  appName,
}) {
  const closeButtonLinkName = getSplitScreenCloseButtonLinkName(appName)
  const [t] = useTranslation()
  return (
    <div css={windowContainerCss}>
      {splitScreenIsOpen && Boolean(SplitScreenImageViewer) && (
        <Elevation z={4} css={windowCss}>
          <Row css={windowHeaderCss} gutters="nano" guttersY="none">
            <H2 size="H6" growX noShrinkX>
              {splitScreenTitleText}
            </H2>
            {setSplitScreenIsOpen && (
              <IconButton
                title={t('split-screen.close.button', 'Close split screen')}
                data-testid="close-split-screen-button"
                noShrinkX
                size="sm"
                emphasis="low"
                Icon={MenuClose}
                onClick={() => setSplitScreenIsOpen(false)}
                linkName={closeButtonLinkName}
                dense
              />
            )}
          </Row>
          <div css={viewerWrapperCss}>{SplitScreenImageViewer}</div>
          {splitScreenRibbonReady && SplitScreenRibbonViewer}
        </Elevation>
      )}
      <Elevation z={4} css={windowCss}>
        {splitScreenIsOpen && (
          <Row css={windowHeaderCss}>
            <H2 size="H6">{mainImageTitleText}</H2>
          </Row>
        )}
        <div css={viewerWrapperCss}>{MainImageViewer}</div>
        {mainRibbonReady && MainRibbonViewer}
      </Elevation>
    </div>
  )
}

export function getSplitScreenCloseButtonLinkName(appName) {
  switch (appName) {
    case 'Rii':
      return 'close-split-screen-button-rii'
    case 'FamilyReview':
      return 'close-split-screen-button-family-review'
    default:
      throw new Error(
        "appName must be tracked in this switch statement for readabilitie's sake... (just add a new case)"
      )
  }
}
